import { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { getUsers, joinRefereePool, updateMyPoolEntry, updateRefereeEntry } from '../../api/client';
import { useI18n, formatDay } from '../../i18n';
import { noviceSeats } from '../../utils/officialNovice';
import { useAuthStore } from '../../store/auth';
import { ClipboardIcon } from '../shared/Icons';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalFooter from '../shared/ModalFooter';
import Field from '../shared/Field';
import ConfirmModal from '../shared/ConfirmModal';
import { useLoad } from '../../hooks/useLoad';
import { personName } from '../../utils/personName';
import type { TournamentReferee, UserListRow } from '../../types';

interface RefereeModalProps {
  mode: 'join' | 'edit' | 'add';
  tournamentId: string;
  ref_?: TournamentReferee;
  tournamentDays: string[];
  isMultiDay: boolean;
  existingIds?: string[];
  onSaved: () => void | Promise<void>;
  onRemove?: () => void;
  onClose: () => void;
}

export default function RefereeModal({
  mode, tournamentId, ref_, tournamentDays, isMultiDay, existingIds,
  onSaved, onRemove, onClose, }: RefereeModalProps) {
  const { showError } = useToast();
  const afterWrite = useAfterWrite();
  const { t } = useI18n();
  const { hasRole } = useAuthStore();
  const isJoin = mode === 'join';
  const isAdd = mode === 'add';
  const [inJuryPool, setInJuryPool] = useState<boolean>(ref_?.in_jury_pool ?? false);
  // A day that left the tournament has no checkbox, so the save refused it
  // with REFEREE_DATES_INVALID (#455 review): keep the tournament's days only.
  const [dates, setDates]           = useState<string[]>((ref_?.available_dates ?? tournamentDays).filter(d => tournamentDays.includes(d)));
  // #605, #664 — the lock of Save: the join, the add and the edit end it in `finally`.
  const saving = useBusy();
  const [selectedUserId, setSelectedUserId] = useState('');
  // #589 — the first question of a removal; OfficialsTab asks the second one.
  const [confirmRemove, setConfirmRemove] = useState(false);

  const { user: currentUser } = useAuthStore();
  const isSelf = !isAdd && (!ref_ || currentUser?.id === ref_?.id);
  // #641, #642 — the team and the novice status come from the profile of the
  // official (the team history, the computed status). The form shows them as
  // text; the server sends them only to a viewer who may see them.
  const showProfile = !isAdd && !isJoin && !!ref_ && ref_.team_id !== undefined;
  const novice = ref_ ? noviceSeats(ref_, t) : t.refereeModal.noviceNone;
  // #589 — Add Referee lists the referee accounts and the jury accounts.
  // A failed load left an empty select with no message: onError shows it.
  // OverviewTab shows the same read the same way.
  const { data: addUsers } = useLoad(
    isAdd ? () => getUsers({ role: ['referee', 'jury'] }).then(r => r.data) : null,
    [],
    { context: 'RefereeModal:users', initial: [] as UserListRow[], expected: () => true, onError: err => showError(err) },
  );
  // #589 — the jury pool takes an account with the jury role or the admin
  // role (the server refuses the others with JURY_POOL_NEEDS_JURY_ROLE).
  const selectedUser = addUsers.find(u => u.id === selectedUserId);
  const canJoinJuryPool = isAdd
    ? !!selectedUser?.roles.some(role => role === 'jury' || role === 'admin')
    : isSelf ? hasRole('jury', 'admin') : !!ref_?.can_join_jury_pool;

  async function save() {
    await saving.run(async () => {
      try {
        // #664, F07 — null means every day: a one-day tournament, or an empty list.
        const available_dates = isMultiDay ? (dates.length ? dates : null) : null;
        if (isJoin) {
          await joinRefereePool(tournamentId, { in_jury_pool: canJoinJuryPool && inJuryPool, available_dates });
        } else if (isAdd) {
          if (!selectedUserId) return;
          await joinRefereePool(tournamentId, {
            user_id: selectedUserId, in_jury_pool: canJoinJuryPool && inJuryPool, available_dates,
          });
        } else {
          // A hidden jury box sends nothing: the stored value stays.
          const entry = { ...(canJoinJuryPool ? { in_jury_pool: inJuryPool } : {}), available_dates };
          await (isSelf ? updateMyPoolEntry(tournamentId, entry) : updateRefereeEntry(tournamentId, ref_!.id, entry));
        }
        // #415 — AWAIT it. `onSaved` is async; calling it bare made a failed
        // refresh an unhandled rejection: nothing logged, the list left stale,
        // and the success toast below still fired. The user was told the
        // opposite of what happened, which is the exact symptom #412 produced
        // for a different reason.
      } catch (err) {
        showError(err);
        return;
      }
      // #419 — the mutation committed. A failed refetch is a different fact:
      // the modal still closes, and the user reads a warning, not an error.
      await afterWrite({
        source: 'RefereeModal:save',
        success: isJoin ? t.tournament.joinedPool : isAdd ? t.tournament.refereeAdded : t.tournament.refereeUpdated,
        refresh: async () => onSaved(),
      });
      onClose();
    });
  }

  const title = isJoin ? t.tournament.refereePoolJoinBtn : isAdd ? t.refereeModal.addTitle : t.refereeModal.editTitle;
  const name = ref_ ? personName(ref_.first_name, ref_.last_name) : '';

  // #552 — the shared shell. The box had no max height, so on a short phone
  // Save was out of reach; the destructive "Remove from pool" was a full-width
  // button under the footer, and it is now a text button in the footer (#675).
  return (
    <>
    <ModalBackdrop onClose={onClose} maxWidth="sm" ariaLabel={title}>
      <ModalHeader
        title={title}
        subtitle={!isJoin && !isAdd ? name : undefined}
      />
      <div className="space-y-4">
        {isAdd && (
          <Field label={t.refereeModal.selectReferee}>
            <select className="input" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)} data-testid="referee-select">
              <option value="">{t.refereeModal.choose}</option>
              {addUsers.filter(u => !(existingIds || []).includes(u.id)).map(u => (
                <option key={u.id} value={u.id}>{personName(u.first_name, u.last_name)}</option>
              ))}
            </select>
          </Field>
        )}
        {canJoinJuryPool && (
          <label className="flex items-center gap-2.5 cursor-pointer py-2 px-3 rounded-lg bg-purple-50 border border-purple-200" data-testid="jury-pool-box">
            <input type="checkbox" checked={inJuryPool} onChange={e => setInJuryPool(e.target.checked)} className="w-4 h-4 rounded-sm accent-purple-600" />
            <div>
              <div className="text-sm font-semibold text-purple-800 inline-flex items-center gap-1.5"><ClipboardIcon className="w-4 h-4" /> {t.refereeModal.juryPool}</div>
              <div className="text-xs text-purple-600">{t.refereeModal.juryPoolHint}</div>
            </div>
          </label>
        )}
        {showProfile && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 py-2 px-3 space-y-0.5" data-testid="referee-profile-info">
            <div className="text-sm"><span className="text-gray-500">{t.refereeModal.teamAffiliation}: </span>{ref_!.team_name ?? t.officials.noTeam}</div>
            <div className="text-sm" data-testid="referee-profile-novice"><span className="text-gray-500">{t.refereeModal.novice}: </span>{novice}</div>
            <p className="text-xs text-gray-500">{t.refereeModal.profileInfo}</p>
          </div>
        )}
        {isMultiDay && tournamentDays.length > 0 && (
          <Field label={t.refereeModal.availableDays} group help={t.refereeModal.daysHint}>
            <div className="flex flex-wrap gap-2">
              {tournamentDays.map(day => (
                <label key={day} className="flex items-center gap-1.5 cursor-pointer py-1">
                  <input type="checkbox" checked={dates.includes(day)} data-testid={`referee-day-${day}`}
                    onChange={e => setDates(prev => e.target.checked ? [...prev, day] : prev.filter(d => d !== day))}
                    className="w-4 h-4 rounded-sm accent-gray-900" />
                  <span className="text-sm">{formatDay(day, 'EEE dd MMM')}</span>
                </label>
              ))}
            </div>
          </Field>
        )}
      </div>
      <ModalFooter
        actions={onRemove && !isJoin ? [{ label: t.tournament.removeFromPool, onClick: () => setConfirmRemove(true), destructive: true, disabled: saving.busy, testId: 'referee-remove' }] : undefined}
        onSave={save}
        busy={saving.busy}
        saveLabel={isJoin ? t.tournament.refereePoolJoinBtn : isAdd ? t.common.add : t.common.save}
        saveDisabled={isAdd && !selectedUserId}
        onCancel={onClose}
      />
    </ModalBackdrop>
    {confirmRemove && onRemove && (
      <ConfirmModal destructive title={t.tournament.removeRefereeTitle}
        message={t.tournament.removeNamedRefereeConfirm.replace('{name}', name)}
        confirmLabel={t.common.remove} testId="remove-referee-named-confirm"
        onConfirm={() => { setConfirmRemove(false); onRemove(); onClose(); }}
        onCancel={() => setConfirmRemove(false)} />
    )}
    </>
  );
}
